import { Suspense, useEffect, useState } from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import {
  Button,
  ButtonAppearance,
  Checkbox,
} from '@atlassian/learning-components';
import Badge from '@atlaskit/badge';
import { NormalizedProduct } from 'utils/useMapProducts';

interface SelectedProduct {
  [key: string]: boolean; // The key is the item's string ID
}

const ManageProducts = ({
  preselectedProducts,
  allProducts,
  changeHandler,
  closeModal,
}: {
  preselectedProducts: string | null;
  allProducts: NormalizedProduct[];
  changeHandler: (products: string) => void;
  closeModal: () => void;
}) => {
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct>({});

  const initialSelectedProducts = preselectedProducts
    ? preselectedProducts.split(',')
    : null;

  useEffect(() => {
    const initialState: SelectedProduct = {};
    allProducts.forEach((product) => {
      if (
        initialSelectedProducts &&
        initialSelectedProducts.includes(product.id)
      ) {
        initialState[product.id] = true;
      } else {
        initialState[product.id] = false;
      }
    });
    setSelectedProducts(initialState);
  }, [allProducts]);

  const handleCheckboxChange = (id: string) => {
    setSelectedProducts((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  };

  const clearCheckboxes = () => {
    setSelectedProducts((prevState) => {
      const newState: SelectedProduct = {};
      Object.keys(prevState).forEach((key) => {
        newState[key] = false;
      });
      return newState;
    });
  };

  const onSubmit = () => {
    const selectedProductIds = Object.entries(selectedProducts)
      .filter(([, isSelected]) => isSelected)
      .map(([id]) => id);
    changeHandler(selectedProductIds.join(','));
    closeModal();
  };

  const selectedProductsCount = Object.values(selectedProducts).filter(
    (isSelected) => isSelected,
  ).length;

  const totalProductsCount = Object.entries(selectedProducts).length;
  const leftColumnCount = totalProductsCount
    ? Math.ceil(totalProductsCount / 2)
    : 0;

  return (
    <Suspense fallback={<div>{'Loading...'}</div>}>
      <div css={modalContainerStyles}>
        <div css={headerStyles}>
          <h3 css={titleStyles}>{'Manage your Atlassian apps'}</h3>
          <div css={selectedCountContainerStyles}>
            <span css={selectedCountTextStyles}>{'Selected: '}</span>
            <Badge appearance="primary">{selectedProductsCount}</Badge>
          </div>
        </div>
        <div css={productsStyles}>
          <div css={productsColumnStyles}>
            {allProducts.slice(0, leftColumnCount).map((product) => {
              if (product && product.title) {
                return (
                  <div key={product.id + 'container'} css={checkboxStyles}>
                    <Checkbox
                      key={product.id}
                      label={product.title}
                      isChecked={selectedProducts[product.id] || false}
                      onChange={() => {
                        handleCheckboxChange(product.id);
                      }}
                    />
                  </div>
                );
              }
            })}
          </div>
          <div>
            <div css={productsColumnStyles}>
              {allProducts.slice(leftColumnCount).map((product) => {
                if (product && product.title) {
                  return (
                    <div key={product.id + 'container'} css={checkboxStyles}>
                      <Checkbox
                        key={product.id}
                        label={product.title}
                        isChecked={selectedProducts[product.id] || false}
                        onChange={() => {
                          handleCheckboxChange(product.id);
                        }}
                      />
                    </div>
                  );
                }
              })}
            </div>
          </div>
        </div>
        <div css={buttonsStyles}>
          <button
            type="button"
            css={clearButtonStyles}
            onClick={clearCheckboxes}
          >
            {'Clear all'}
          </button>
          <Button
            appearance={ButtonAppearance.PRIMARY}
            label="Save Changes"
            onClick={onSubmit}
          ></Button>
        </div>
      </div>
    </Suspense>
  );
};

const modalContainerStyles = css({
  width: '720px',
  height: '598px',
  display: 'flex',
  flexDirection: 'column',
  '@media (max-width: 767px)': {
    width: '100%',
    height: '100%',
  },
});

const headerStyles = css({
  flexShrink: 0,
  paddingBottom: '24px',
  flex: '0 1 auto',
});

const titleStyles = css({
  fontFamily: token('font.family.brand.heading'),
  fontSize: '32px',
  fontWeight: token('font.weight.semibold'),
  lineHeight: '40px',
});

const productsStyles = css({
  display: 'flex',
  gap: '108px',
  padding: '24px 0px 24px',
  borderTop: `2px solid ${token('color.border')}`,
  borderBottom: `2px solid ${token('color.border')}`,
  flex: '1 1 auto',
  overflowY: 'auto',
  lineHeight: '20px',
  '@media (max-width: 767px)': {
    flexDirection: 'column',
    gap: '16px',
  },
});

const buttonsStyles = css({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '24px',
  flex: '0 1 auto',
  padding: '16px 0 32px',
  '@media (min-width: 768px)': {
    padding: '24px 16px 0px',
  },
});

const clearButtonStyles = css({
  textDecoration: 'none',
  color: token('color.text.brand'),
  fontSize: '16px',
  fontWeight: token('font.weight.semibold'),
  display: 'flex',
  alignItems: 'center',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
});

const selectedCountContainerStyles = css({
  marginTop: '8px',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

const selectedCountTextStyles = css({
  fontFamily: 'Inter',
  color: token('color.text.subtle'),
  fontSize: '16px',
  lineHeight: '24px',
});

const productsColumnStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

const checkboxStyles = css({});

export default ManageProducts;
